import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { PERMISSIONS } from '../shared/constants/permissions';
import { firestore } from './firebase';

/**
 * Shares a tender with another user by re-encrypting the Tender Key (TEK)
 * with the target user's Public Key.
 */
export const shareTender = async (
  tenderId: string,
  targetUserId: string,
  targetPublicKeyJWK: JsonWebKey
): Promise<{ success: boolean; message: string }> => {
  if (!tenderId || !targetUserId || !targetPublicKeyJWK) {
    throw new Error('Missing required parameters for sharing.');
  }

  // A. Fetch Data
  const tenderRef = doc(firestore, 'tenders', tenderId);

  try {
    const snap = await getDoc(tenderRef);
    if (!snap.exists()) {
      throw new Error('Tender not found');
    }
  } catch (_e) {
    throw new Error('Failed to fetch tender data.');
  }

  // D. Update Tender Shared List
  await updateDoc(tenderRef, {
    sharedWith: arrayUnion(targetUserId),
  });

  return { success: true, message: 'Tender shared successfully.' };
};

/**
 * Shares a tender securely with an external partner user.
 * It assigns restricted member permissions (partner role equivalents).
 */
export const shareTenderWithPartner = async (
  tenderId: string,
  targetUserId: string,
  targetPublicKeyJWK: JsonWebKey
): Promise<{ success: boolean; message: string }> => {
  // First, do the standard envelope encryption sharing
  const shareResult = await shareTender(tenderId, targetUserId, targetPublicKeyJWK);
  if (!shareResult.success) {
    return shareResult;
  }

  // Second, grant specific partner permissions.
  // We allow them to view the tender, view files, and perhaps edit drafts or specific sections.
  // For safety, we only grant view access and allow them to upload to their specific library path.
  const tenderRef = doc(firestore, 'tenders', tenderId);

  // Assign permissions explicitly for the partner
  // Default is restricted to only allowing them to add files/edit their sections
  // Further permissions can be added explicitly by the Tender Owner via the UI
  const partnerPermissions = [PERMISSIONS.TENDER_EDIT];

  await updateDoc(tenderRef, {
    [`memberPermissions.${targetUserId}`]: partnerPermissions,
  });

  return { success: true, message: 'Tender securely shared with partner.' };
};

/**
 * Automatically shares a tender with all Admins and relevant Team Leads.
 */
export const shareTenderWithWorkspaceAdmins = async (tenderId: string): Promise<void> => {
  const { auth } = await import('./firebase');
  const currentUser = auth?.currentUser;
  if (!currentUser) {
    return;
  }

  const tenderRef = doc(firestore, 'tenders', tenderId);
  const userRef = doc(firestore, 'users', currentUser.uid);

  try {
    const [tenderSnap, userSnap] = await Promise.all([getDoc(tenderRef), getDoc(userRef)]);

    if (!tenderSnap.exists() || !userSnap.exists()) {
      return;
    }

    const tenderData = tenderSnap.data();

    const clientId = tenderData.clientId;

    if (!clientId) {
      return;
    }

    // We need to fetch all users in the same client who are Admins or Super Admins
    // And potentially the Team Lead of the specific team
    const usersRef = collection(firestore, 'users');
    const qAdmins = query(
      usersRef,
      where('clientId', '==', clientId),
      where('role', '==', 'client_admin')
    );

    const adminDocs = await getDocs(qAdmins);
    const uidsToShareWith: string[] = [];
    for (const docSnap of adminDocs.docs) {
      if (docSnap.id !== currentUser.uid) {
        uidsToShareWith.push(docSnap.id);
      }
    }
    if (uidsToShareWith.length > 0) {
      await updateDoc(tenderRef, {
        sharedWith: arrayUnion(...uidsToShareWith),
      });
    }
  } catch (_error) {
    // Error occurred
  }
};

/**
 * Removes a user's access to a tender by removing them from the sharedWith array.
 * Note: Their encryption key in the subcollection is not deleted to preserve history/audit,
 * but they will no longer be able to read the document due to Firestore Security Rules.
 */
export const unshareTender = async (
  tenderId: string,
  targetUserId: string
): Promise<{ success: boolean; message: string }> => {
  if (!tenderId || !targetUserId) {
    throw new Error('Missing required parameters for unsharing.');
  }

  const { auth, firestore: db } = await import('./firebase');
  const currentUser = auth?.currentUser;
  if (!currentUser) {
    throw new Error('Not authenticated.');
  }

  const tenderRef = doc(db, 'tenders', tenderId);

  // Use arrayRemove to pull them from the sharedWith array.
  await updateDoc(tenderRef, {
    sharedWith: arrayRemove(targetUserId),
    [`memberPermissions.${targetUserId}`]: deleteField(),
  });

  return { success: true, message: 'User access removed successfully.' };
};
